/**
 * Erkennung der Ausführungs-Umgebung der Web-App.
 *
 * Drei wichtige Zustände unterscheiden:
 *
 *  - "browser"   Normale Webseite im Chrome/Firefox/Safari
 *  - "pwa"       Über "Zum Startbildschirm hinzufügen" installierte PWA
 *                (display-mode: standalone, aber kein TWA-Referrer)
 *  - "twa"       Echte Android-App (Trusted Web Activity) — erkennbar am
 *                document.referrer "android-app://<package>/..."
 *
 * Versions-Erkennung:
 * Die TWA übergibt beim Start ihre Version als URL-Parameter
 * `?v=1.0.0` (konfiguriert im TWA-Manifest). Wir lesen den Parameter
 * beim ersten Aufruf aus und persistieren ihn in localStorage, damit
 * spätere Aufrufe (ohne den Parameter) den Wert weiterhin kennen.
 */

const STORAGE_KEY_APP_VERSION = 'shiftsync.app.version';
const STORAGE_KEY_LAUNCH_SOURCE = 'shiftsync.app.launchSource';

export type PlatformKind = 'browser' | 'pwa' | 'twa';

/** Erkennt, ob die App im "Standalone"-Modus läuft (PWA oder TWA). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    // iOS Safari Sondersyntax
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Erkennt, ob die App als TWA (echte Android-APK) läuft. */
export function isTwa(): boolean {
  if (typeof document === 'undefined') return false;

  // Primär: document.referrer wird bei TWA-Launch auf "android-app://..." gesetzt
  if (document.referrer.startsWith('android-app://')) return true;

  // Backup: ein einmal als TWA erkannter Launch wird in localStorage festgehalten,
  // sodass spätere In-App-Navigationen (mit leerem referrer) weiterhin als TWA gelten.
  try {
    return localStorage.getItem(STORAGE_KEY_LAUNCH_SOURCE) === 'twa';
  } catch {
    return false;
  }
}

/** Liefert die aktuelle Plattform-Kategorie. */
export function getPlatform(): PlatformKind {
  if (isTwa()) return 'twa';
  if (isStandalone()) return 'pwa';
  return 'browser';
}

export function platformLabel(p: PlatformKind = getPlatform()): string {
  switch (p) {
    case 'twa':     return 'Android-App';
    case 'pwa':     return 'PWA (Browser-Verknüpfung)';
    case 'browser': return 'Browser';
  }
}

/**
 * Liest die installierte App-Version aus localStorage, falls die TWA jemals
 * einen `?v=X.Y.Z`-Parameter beim Start mitgegeben hat. Sonst null.
 */
export function getInstalledAppVersion(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_APP_VERSION);
  } catch {
    return null;
  }
}

/**
 * Beim App-Start einmal aufrufen. Liest den `?v=X.Y.Z`-Query-Param und
 * persistiert ihn in localStorage. Setzt zusätzlich das `launchSource`-Flag
 * auf "twa", sobald wir den Parameter sehen — denn nur die TWA hängt ihn an.
 *
 * Räumt den Param danach nicht aus der URL (das macht React-Router beim
 * ersten Navigieren). Aber URL-State wird nicht durch ein PushState-replace
 * geändert, weil React-Router das selbst übernimmt.
 */
export function captureAppVersionFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('v');
    const isApp = params.get('app') === '1';

    if (v) {
      localStorage.setItem(STORAGE_KEY_APP_VERSION, v);
    }
    if (isApp || v) {
      localStorage.setItem(STORAGE_KEY_LAUNCH_SOURCE, 'twa');
    } else if (document.referrer.startsWith('android-app://')) {
      // referrer reicht uns als TWA-Indiz aus
      localStorage.setItem(STORAGE_KEY_LAUNCH_SOURCE, 'twa');
    }
  } catch {
    // localStorage in Privatemode → silently ignore
  }
}

/**
 * Liefert Informationen über die *aktuell verfügbare* App-Version vom Server
 * (statisches JSON unter /app-version.json).
 *
 * Nicht aggressiv cachen — sonst sieht der User nach einem Release immer noch
 * die alte Version.
 */
export interface AppVersionInfo {
  /** Aktuellste APK-Version, z. B. "1.0.0" */
  latest: string;
  /** Pfad zum APK-Download (relativ oder absolut). */
  downloadUrl: string;
  /** Datum der Veröffentlichung (TT.MM.JJJJ oder ISO). */
  releaseDate?: string;
  /** Liste mit den Änderungen seit dem letzten Release. */
  changelog?: string[];
  /** SHA256-Fingerprint der Signatur (zur Identifikation der korrekten APK). */
  sha256?: string;
}

export async function fetchLatestAppVersion(): Promise<AppVersionInfo | null> {
  try {
    const res = await fetch('/app-version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as AppVersionInfo;
  } catch {
    return null;
  }
}
